import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { FlexibleDataGrid, type FlexibleDataGridColumn } from '../components/FlexibleDataGrid';
import { useAuth } from '../contexts/AuthContext';
import { generalList2Service } from '../services/generalList2Service';

type PriceMismatchStatus = 'pending' | 'approved' | 'rejected';

type PriceMismatchRow = {
  id: string;
  rowKey: string;
  sevkKodu: string;
  planKodu: string;
  yuklemeMusterisi: string;
  yuklemeLokasyon: string;
  bosaltmaMusterisi: string;
  teslimLokasyon: string;
  durum: string;
  islemDurum: string;
  navlun: number;
  bazFiyat: number;
  stopCount: number;
  extraStopCount: number;
  extraStopFeeTotal: number;
  allowedPrice: number;
  fark: number;
  approvalStatus: PriceMismatchStatus;
  rejectionReason: string;
  decidedByName: string;
  decidedAt: string;
  updatedAt: string;
};

const STATUS_TABS: Array<{ key: PriceMismatchStatus; label: string }> = [
  { key: 'pending', label: 'Onay Bekliyor' },
  { key: 'approved', label: 'Onaylandı' },
  { key: 'rejected', label: 'Reddedildi' },
];

const formatMoney = (value: number) => `${Number(value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR');
};

const getDurumLabel = (value?: string | null) => {
  const normalized = String(value || '').trim().toLocaleLowerCase('tr-TR');
  if (normalized === 'pending') return 'Tedarik Bekliyor';
  if (normalized === 'open') return 'Teklif Toplanıyor';
  if (normalized === 'assigned') return 'Tedarik Edildi';
  if (normalized === 'in-transit') return 'Yolda';
  if (normalized === 'delivered') return 'Teslim Edildi';
  if (normalized === 'cancelled') return 'İptal';
  return value || '-';
};

const getIslemDurumLabel = (value?: string | null) => {
  const normalized = String(value || '').trim().toLocaleLowerCase('tr-TR');
  const labels: Record<string, string> = {
    'tedarik-bekleniyor': 'Tedarik Bekleniyor',
    'spot-teklif-bekleniyor': 'Spot Teklif Bekleniyor',
    'atandi-islem-bekliyor': 'Atandı, İşlem Bekliyor',
    'yukleme-bekliyor': 'Yükleme Bekliyor',
    'yukleme-devam': 'Yükleme Devam Ediyor',
    'yukleme-tamamlandi': 'Yükleme Tamamlandı',
    'tasima-yolunda': 'Teslim Yolunda',
    'teslim-noktasina-ulasildi': 'Teslim Noktasına Ulaşıldı',
    'bosaltma-bekliyor': 'Boşaltma Bekliyor',
    'bosaltma-devam': 'Boşaltma Devam Ediyor',
    'bosaltma-tamamlandi': 'Boşaltma Tamamlandı',
  };
  return labels[normalized] || value || '-';
};

const mapRow = (item: any, index: number): PriceMismatchRow => ({
  id: String(item.id || ''),
  rowKey: `${item.id || ''}::${item.shipment_no || ''}::${index}`,
  sevkKodu: item.shipment_no || '-',
  planKodu: item.plan_code || '-',
  yuklemeMusterisi: item.pickup_name || '-',
  yuklemeLokasyon: [item.pickup_city, item.pickup_district].filter(Boolean).join(' / ') || '-',
  bosaltmaMusterisi: item.delivery_name || '-',
  teslimLokasyon: [item.delivery_city, item.delivery_district].filter(Boolean).join(' / ') || '-',
  durum: getDurumLabel(item.status),
  islemDurum: getIslemDurumLabel(item.process_status),
  navlun: Number(item.navlun || 0),
  bazFiyat: Number(item.allowed_price || item.base_price || 0),
  stopCount: Number(item.stop_count || 0),
  extraStopCount: Number(item.extra_stop_count || 0),
  extraStopFeeTotal: Number(item.extra_stop_fee_total || 0),
  allowedPrice: Number(item.allowed_price || 0),
  fark: Number(item.mismatch_amount || 0),
  approvalStatus: (item.approval_status || 'pending') as PriceMismatchStatus,
  rejectionReason: item.rejection_reason || '',
  decidedByName: item.decided_by_name || '',
  decidedAt: item.decided_at || '',
  updatedAt: item.updated_at || '',
});

const columns: FlexibleDataGridColumn<PriceMismatchRow>[] = [
  { key: 'sevkKodu', label: 'Sipariş #', width: 130, sortable: true },
  { key: 'planKodu', label: 'Plan Kodu', width: 120, sortable: true },
  { key: 'yuklemeMusterisi', label: 'Yükleme Müşterisi', width: 220, sortable: true },
  { key: 'yuklemeLokasyon', label: 'Yükleme İl / İlçe', width: 180, sortable: true },
  { key: 'bosaltmaMusterisi', label: 'Boşaltma Müşterisi', width: 220, sortable: true },
  { key: 'teslimLokasyon', label: 'Teslim İl / İlçe', width: 180, sortable: true },
  { key: 'durum', label: 'Durum', width: 140, sortable: true },
  { key: 'islemDurum', label: 'İşlem Durumu', width: 170, sortable: true },
  { key: 'navlun', label: 'Navlun', width: 130, sortable: true, render: (row) => formatMoney(row.navlun), getValue: (row) => row.navlun },
  { key: 'bazFiyat', label: 'Baz Fiyat', width: 130, sortable: true, render: (row) => formatMoney(row.bazFiyat), getValue: (row) => row.bazFiyat },
  { key: 'stopCount', label: 'Durak Sayısı', width: 110, sortable: true, getValue: (row) => row.stopCount },
  { key: 'extraStopFeeTotal', label: 'Ek Durak Bedeli', width: 150, sortable: true, render: (row) => formatMoney(row.extraStopFeeTotal), getValue: (row) => row.extraStopFeeTotal },
  { key: 'allowedPrice', label: 'Satınalma Max Fiyat', width: 170, sortable: true, render: (row) => formatMoney(row.allowedPrice), getValue: (row) => row.allowedPrice },
  { key: 'fark', label: 'Fark', width: 130, sortable: true, render: (row) => <span className="font-semibold text-rose-700">{formatMoney(row.fark)}</span>, getValue: (row) => row.fark },
  { key: 'rejectionReason', label: 'Fiyat Reddi', width: 260, render: (row) => row.rejectionReason?.trim() || '-' },
  { key: 'decidedByName', label: 'İşlem Yapan', width: 180, sortable: true, render: (row) => row.decidedByName || '-' },
  { key: 'decidedAt', label: 'İşlem Tarihi', width: 180, sortable: true, render: (row) => formatDateTime(row.decidedAt), getValue: (row) => row.decidedAt },
  { key: 'updatedAt', label: 'Güncelleme', width: 180, sortable: true, render: (row) => formatDateTime(row.updatedAt), getValue: (row) => row.updatedAt },
];

export function GeneralList2PriceMismatch({ includeLocked }: { includeLocked: boolean }) {
  const { user } = useAuth();
  const [activeStatus, setActiveStatus] = useState<PriceMismatchStatus>('pending');
  const [rows, setRows] = useState<PriceMismatchRow[]>([]);
  const [stats, setStats] = useState<Record<PriceMismatchStatus, number>>({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [rejectRow, setRejectRow] = useState<PriceMismatchRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [savingActionId, setSavingActionId] = useState<string | null>(null);

  const canApprove = useMemo(() => {
    const roles = Array.from(new Set([user?.role, ...(user?.roles || [])].filter(Boolean)));
    return Boolean(user?.canPriceMismatchApprove || roles.includes('admin') || roles.includes('super-admin'));
  }, [user]);

  const loadRows = async () => {
    try {
      setLoading(true);
      const response: any = await generalList2Service.getPriceMismatches({ approvalStatus: activeStatus, includeLocked });
      const list = Array.isArray(response?.data) ? response.data : [];
      const nextStats = response?.stats || {};
      setRows(list.map((item: any, index: number) => mapRow(item, index)));
      setStats({
        pending: Number(nextStats.pending || 0),
        approved: Number(nextStats.approved || 0),
        rejected: Number(nextStats.rejected || 0),
      });
    } catch (error: any) {
      toast.error(error?.message || 'Uyumsuz fiyat listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRows();
  }, [activeStatus, includeLocked]);

  const handleApprove = async (row: PriceMismatchRow) => {
    if (!confirm(`${row.sevkKodu} için navlun baz fiyat üstü onaylansın mı?`)) return;
    try {
      setSavingActionId(row.id);
      await generalList2Service.approvePriceMismatch(row.id);
      toast.success('Uyumsuz fiyat onaylandı');
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'Onay kaydedilemedi');
    } finally {
      setSavingActionId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectRow) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error('Reddetme nedeni yazılmalıdır');
      return;
    }
    try {
      setSavingActionId(rejectRow.id);
      await generalList2Service.rejectPriceMismatch(rejectRow.id, reason);
      toast.success('Uyumsuz fiyat reddedildi');
      setRejectRow(null);
      setRejectReason('');
      await loadRows();
    } catch (error: any) {
      toast.error(error?.message || 'Red kaydedilemedi');
    } finally {
      setSavingActionId(null);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col p-4">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((tab) => {
          const active = activeStatus === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveStatus(tab.key)}
              className={`rounded-xl border px-4 py-2 text-left text-sm font-semibold transition ${active ? 'border-orange-400 bg-orange-50 text-orange-900 ring-2 ring-orange-200' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              <div>{tab.label}</div>
              <div className="mt-1 text-xs font-medium text-slate-500">{stats[tab.key]} kayıt</div>
            </button>
          );
        })}
        <button type="button" onClick={() => void loadRows()} disabled={loading} className="ml-auto inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Yenile
        </button>
      </div>

      <FlexibleDataGrid
        title="Uyumsuz Fiyatlar (Spot)"
        rows={rows}
        columns={columns}
        getRowKey={(row) => row.rowKey}
        loading={loading}
        emptyMessage="Bu sekmede uyumsuz spot fiyat kaydı bulunamadı."
        searchable
        selectable={false}
        initialPageSize={50}
        pageSizeOptions={[25, 50, 100, 200]}
        rowActions={(row) => (
          activeStatus === 'pending' && canApprove ? (
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => void handleApprove(row)} disabled={savingActionId === row.id} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                <Check className="h-3.5 w-3.5" /> Onayla
              </button>
              <button type="button" onClick={() => { setRejectRow(row); setRejectReason(row.rejectionReason || ''); }} disabled={savingActionId === row.id} className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                <X className="h-3.5 w-3.5" /> Reddet
              </button>
            </div>
          ) : <span className="text-xs text-slate-400">-</span>
        )}
      />

      {rejectRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-3 text-lg font-semibold text-slate-900">Fiyat Reddi</div>
            <div className="mb-3 text-sm text-slate-600">
              <span className="font-semibold">{rejectRow.sevkKodu}</span> için reddetme nedeni yazın.
            </div>
            <textarea
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              rows={5}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-400"
              placeholder="Reddetme nedeni"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setRejectRow(null); setRejectReason(''); }} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Vazgeç
              </button>
              <button type="button" onClick={() => void handleReject()} disabled={savingActionId === rejectRow.id} className="inline-flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                {savingActionId === rejectRow.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Reddet
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
